import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, formatApiError } from "@/lib/api";
import { formatCZK, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { CreditCard, ReceiptText, LifeBuoy, ExternalLink, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { UpgradeDialog } from "./UpgradeDialog";

const DS_CHIP = {
  demo_bezi: "bg-secondary text-muted-foreground",
  demo_vyprselo: "bg-brand/15 text-brand",
  plati: "bg-emerald-500/15 text-emerald-500",
  ceka_uhrada: "bg-brand/15 text-brand",
  ukonceno: "bg-rose-500/15 text-rose-500",
  pozastaveno: "border border-rose-500/60 text-rose-500",
};

function UsageBar({ label, used, max }) {
  // max null = neomezeno (dekorativních 18 %); max 0 = není v tarifu (126/E);
  // jinak podíl — used/0 se nikdy nesmí dostat do width.
  const notInPlan = max === 0;
  const pct = max == null ? 18 : notInPlan ? 0 : Math.min(100, Math.round((used / max) * 100));
  const hot = max != null && max > 0 && pct >= 80;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className={`font-semibold tabular-nums whitespace-nowrap ${notInPlan ? "text-muted-foreground" : ""}`}>
          {notInPlan
            ? (used > 0 ? `Není v tarifu · ${used} z dema` : "Není v tarifu")
            : <>{used}{max == null ? " · neomezeno" : ` / ${max}`}</>}
        </span>
      </div>
      <div className="mt-1.5 h-2 rounded-full bg-secondary overflow-hidden">
        <div className={`h-full rounded-full ${hot ? "bg-amber-500" : "bg-brand"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// Tab Předplatné — jen okno, ne pokladna. Tarif nastavuje superadmin,
// karta se řeší ve Stripe portálu. Vrací dvě Section do mřížky Settings.
export function SubscriptionPanel({ Section }) {
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [upg, setUpg] = useState(null);        // {current, options, can_self_upgrade}
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    api.get("/billing/summary").then((r) => setData(r.data)).catch((e) => setErr(apiError(e)));
    api.get("/billing/upgrade-options").then((r) => setUpg(r.data)).catch(() => setUpg(null));
  }, []);

  const onUpgraded = () => {
    refresh();
    // Stav firmy zapisuje až Stripe webhook (~1–2 s) — refetch s odstupem,
    // jinak karta Tarif ukáže starý plán.
    const refetch = async () => {
      const r = await api.get("/billing/summary"); setData(r.data);
      const o = await api.get("/billing/upgrade-options"); setUpg(o.data);
      refresh();
    };
    setTimeout(refetch, 2500);
    setTimeout(refetch, 6000);
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  if (err) return <p className="text-sm text-destructive" data-testid="subscription-error">{err}</p>;
  if (!data) return <p className="text-sm text-muted-foreground" data-testid="subscription-loading">Načítám předplatné…</p>;

  const ds = data.display_status || {};
  const isDemo = (data.plan || "demo") === "demo";
  const trialDays = data.trial_ends_at ? Math.max(0, Math.ceil((new Date(data.trial_ends_at) - Date.now()) / 86400000)) : null;

  return (
    <>
      {/* Historie plateb — v kódu první, na desktopu vlevo (mřížka plní zleva) */}
      <Section icon={ReceiptText} title="Historie plateb" testid="subscription-payments">
        {data.payments.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="payments-empty">
            {isDemo ? "Zatím žádná platba — demo je zdarma." : "Zatím tu nejsou žádné platby přes Stripe."}
          </p>
        ) : (
          <div>
            {data.payments.map((p, i) => (
              <div key={i} className={`flex items-center gap-2 py-2.5 ${i > 0 ? "border-t border-border" : ""}`} data-testid={`payment-row-${i}`}>
                <div className="min-w-0 flex-1 flex flex-col sm:flex-row sm:items-center sm:gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm tabular-nums text-muted-foreground">{formatDate(p.date)}</span>
                    <span className="text-sm font-bold tabular-nums whitespace-nowrap">{formatCZK(p.amount)}</span>
                    <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500 text-[11px] font-bold">Zaplaceno</span>
                  </div>
                  <span className="text-xs text-muted-foreground truncate">{p.description}</span>
                </div>
                {p.invoice_url && (
                  <a href={p.invoice_url} target="_blank" rel="noopener noreferrer" className="shrink-0 text-muted-foreground hover:text-foreground" data-testid={`payment-link-${i}`}>
                    <ExternalLink size={15} />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="mt-5 pt-4 border-t border-border" data-testid="subscription-help">
          <div className="text-sm font-bold flex items-center gap-1.5 mb-1"><LifeBuoy size={15} className="text-brand" /> Nejde platba?</div>
          <p className="text-sm text-muted-foreground">Zatím se nám nepodařilo spárovat platbu za toto období? Jakmile platba dorazí, přístup se obnoví obratem. Ozvěte se nám: <a href="tel:+420728398282" className="font-semibold text-foreground">+420 728 398 282</a> · <a href="mailto:petr@proyekty.cz" className="font-semibold text-foreground">petr@proyekty.cz</a></p>
        </div>
      </Section>

      {/* Tarif — na desktopu vpravo */}
      <Section icon={CreditCard} title="Tarif" testid="subscription-plan">
        <div className="space-y-4">
          <div>
            <div className="text-2xl sm:text-3xl font-extrabold whitespace-nowrap" data-testid="subscription-plan-label">{data.plan_label}</div>
            <span className={`inline-block mt-1.5 px-2 py-1 rounded-full text-xs font-bold ${DS_CHIP[ds.key] || "bg-secondary"}`} data-testid="subscription-status-chip">{ds.label}</span>
            {ds.key === "ceka_uhrada" && data.payment_retry_until && (
              <p className="text-xs text-muted-foreground mt-1" data-testid="subscription-retry-line">Další pokus o platbu: {formatDate(data.payment_retry_until)}</p>
            )}
          </div>

          {isDemo ? (
            <div className="text-sm space-y-1.5">
              {trialDays != null && <p>Zbývá <b>{trialDays} dní</b> · končí <b>{formatDate(data.trial_ends_at)}</b></p>}
              <p className="text-muted-foreground">Po skončení dema zůstane 14 dní jen čtení a export. Data nikam nezmizí.</p>
            </div>
          ) : data.next_billing ? (
            <div className="flex items-baseline justify-between gap-3 bg-secondary rounded-xl p-3" data-testid="subscription-next-billing">
              <span className="text-sm text-muted-foreground">Další platba</span>
              <span className="text-sm font-bold tabular-nums whitespace-nowrap">{formatDate(data.next_billing)} · {formatCZK(data.price)}</span>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="subscription-offline-billing">Platba probíhá mimo Stripe.</p>
          )}

          {data.has_stripe && (
            <Button onClick={openPortal} disabled={busy} className="h-11 font-bold w-full md:w-auto" data-testid="subscription-portal-btn">
              <CreditCard size={16} className="mr-1" /> {busy ? "Otevírám…" : "Spravovat platební kartu"}
            </Button>
          )}

          <div className="space-y-3 pt-2" data-testid="subscription-usage">
            <UsageBar label="Lidé" used={data.usage.users.used} max={data.usage.users.limit} />
            <UsageBar label="Doklady přes AI (tento měsíc)" used={data.usage.ocr.used} max={data.usage.ocr.limit} />
            <UsageBar label="Aktivní zakázky" used={data.usage.projects.used} max={data.usage.projects.limit} />
          </div>

          {!isDemo && upg?.can_self_upgrade && upg.options.length > 0 && (
            <div className="pt-3 border-t border-border">
              <Button onClick={() => setUpgradeOpen(true)} className="h-11 w-full md:w-auto font-bold" data-testid="upgrade-open-btn">
                <ArrowUpRight size={16} className="mr-1" /> Povýšit tarif
              </Button>
            </div>
          )}
        </div>
      </Section>

      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen}
        summary={data} upg={upg} onUpgraded={onUpgraded} />
    </>
  );
}
